import { createPortal } from 'react-dom'
import { useSidebar } from './use-sidebar'
import { Button, CloseIcon } from '@/components/atoms'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
  keepMounted?: boolean
  hideCloseButton?: boolean
}

export function Sidebar({
  isOpen,
  onClose,
  children,
  keepMounted,
  hideCloseButton,
}: SidebarProps) {
  const { mounted, visible, hidden } = useSidebar(isOpen, onClose, keepMounted)

  if (!mounted) return <></>

  return createPortal(
    <div
      style={
        hidden
          ? {
              position: 'fixed',
              visibility: 'hidden',
              overflow: 'hidden',
              width: 0,
              height: 0,
            }
          : undefined
      }
    >
      <div
        className={`fixed inset-0 z-(--z-modal) bg-(--overlay-bg) backdrop-blur-sm transition md:z-(--z-menu-overlay) ${visible ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0'}`}
        onClick={onClose}
        aria-hidden="true"
        data-testid="sidebar-backdrop"
      />
      <div
        className={`fixed inset-0 z-(--z-modal) flex md:z-(--z-menu-overlay) ${!visible ? 'pointer-events-none' : ''}`}
        role="dialog"
        aria-modal="true"
        onClick={onClose}
      >
        <div
          className={`relative mt-auto flex h-full w-full flex-col overflow-hidden rounded-t-2xl bg-white py-8 transition-transform duration-300 max-md:max-h-[calc(100dvh-var(--modal-mobile-offset)-var(--sat))] md:ml-auto md:max-h-[calc(100dvh-var(--header-h))] md:max-w-[var(--panel-w)] md:rounded-t-none md:rounded-l-xl ${visible ? 'translate-y-0 md:translate-x-0' : 'translate-y-full md:translate-x-full md:translate-y-0'}`}
          onClick={(e) => e.stopPropagation()}
        >
          {!hideCloseButton && (
            <Button
              onClick={onClose}
              variant="link"
              className="absolute top-8 right-8 z-(--z-header) ml-auto rounded-full text-indigo-900"
              aria-label="Close"
              data-testid="sidebar-close-button"
            >
              <CloseIcon className="h-5 w-5" />
            </Button>
          )}
          <div className="h-full overflow-y-auto overscroll-contain">
            {children}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  )
}
